import Axios from 'axios';
import { getAuthHeader } from '../../../../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_URL;

/**
 * Submit a new review for a question
 */
export const submitReview = async (reviewData) => {
    // Validate review_item_id is a valid integer
    if (!reviewData.review_item_id) {
        throw {
            message: 'Review item ID is required',
            status: 400
        };
    }
    
    const itemId = parseInt(reviewData.review_item_id, 10);
    if (isNaN(itemId)) {
        throw {
            message: 'Review item ID must be a valid integer',
            status: 400
        };
    }
    
    // Ensure review_item_id is sent as integer
    const sanitizedData = {
        ...reviewData,
        review_item_id: itemId
    };
    
    try {
        const response = await Axios.post(
            `${API_BASE_URL}/api/v1/teacher-reviews/submit`,
            sanitizedData,
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getAuthHeader(),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error submitting review:', error);
        throw {
            message: error.response?.data?.message || 'Failed to submit review',
            status: error.response?.status,
            data: error.response?.data
        };
    }
};

/**
 * Update an existing review
 */
export const updateReview = async (reviewId, reviewData) => {
    // Ensure review_item_id is sent as integer if present
    const sanitizedData = { ...reviewData };
    if (sanitizedData.review_item_id) {
        const itemId = parseInt(sanitizedData.review_item_id, 10);
        if (isNaN(itemId)) {
            throw {
                message: 'Review item ID must be a valid integer',
                status: 400
            };
        }
        sanitizedData.review_item_id = itemId;
    }
    
    try {
        const response = await Axios.put(
            `${API_BASE_URL}/api/v1/teacher-reviews/${reviewId}`,
            sanitizedData,
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getAuthHeader(),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error updating review:', error);
        throw {
            message: error.response?.data?.message || 'Failed to update review',
            status: error.response?.status,
            data: error.response?.data
        };
    }
};

/**
 * Get teacher's review history
 */
export const getReviewHistory = async (params = {}) => {
    try {
        const response = await Axios.get(
            `${API_BASE_URL}/api/v1/teacher-reviews/my-reviews`,
            {
                params,
                headers: {
                    Authorization: getAuthHeader(),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching review history:', error);
        throw {
            message: error.response?.data?.message || 'Failed to fetch review history',
            status: error.response?.status,
            data: error.response?.data
        };
    }
};

/**
 * Get summary for a review session
 */
export const getSessionSummary = async (sessionId) => {
    try {
        const response = await Axios.get(
            `${API_BASE_URL}/api/v1/teacher-reviews/sessions/${sessionId}/summary`,
            {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching session summary:', error);
        throw {
            message: error.response?.data?.message || 'Failed to fetch session summary',
            status: error.response?.status,
            data: error.response?.data
        };
    }
};

/**
 * Batch submit multiple reviews
 * Note: This is a helper function that submits reviews sequentially
 * You might want to implement a proper batch API endpoint on the backend
 */
export const batchSubmitReviews = async (reviews, onProgress) => {
    const results = {
        successful: [],
        failed: []
    };
    
    const reviewArray = Object.entries(reviews);
    
    for (let i = 0; i < reviewArray.length; i++) {
        const [itemId, review] = reviewArray[i];
        
        try {
            const result = await submitReview({
                review_item_id: itemId,
                ...review
            });
            
            results.successful.push({
                itemId,
                result
            });
            
            if (onProgress) {
                onProgress({
                    current: i + 1,
                    total: reviewArray.length,
                    percentage: Math.round(((i + 1) / reviewArray.length) * 100)
                });
            }
        } catch (error) {
            results.failed.push({
                itemId,
                error: error.message
            });
        }
        
        // Add a small delay to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return results;
};